/**
 * Importació d'un mestre comercial — la part que proveïdors i clients comparteixen.
 *
 * Els dos mestres es comporten igual allà on importa: tots dos s'identifiquen per
 * un codi propi de l'obrador i, si la fila no en porta, pel NIF; tots dos tenen
 * el NIF únic per obrador entre els actius; tots dos guarden el contacte dins
 * d'un `contact_json`; i tots dos es donen de baixa lògicament en comptes
 * d'esborrar-se. Aquest mòdul és aquella meitat comuna, i els mòduls
 * ./supplierImport i ./clientImport hi posen només el que els distingeix: quins
 * camps porta el full i com es diuen.
 *
 * Pur: sense I/O, sense rellotge, sense atzar.
 *
 * IDENTIFICACIÓ: PRIMER EL CODI, DESPRÉS EL NIF
 * ---------------------------------------------
 * Una fila que porta codi s'identifica NOMÉS pel codi: si aquell codi no hi és,
 * és un registre nou, encara que el NIF coincideixi amb un altre. Buscar també
 * pel NIF en aquest cas voldria dir fusionar dues fitxes que l'obrador ha
 * decidit tenir separades (matriu i delegació comparteixen CIF més sovint del
 * que sembla). Només quan la fila NO porta codi es mira el NIF.
 *
 * EL CODI MIRA ELS DE BAIXA; EL NIF, NOMÉS ELS VIUS
 * --------------------------------------------------
 * L'asimetria de {@link buildMasterImportIndex} no és un descuit. L'índex únic
 * del codi és PARCIAL (només entre els actius), així que el codi d'un registre
 * donat de baixa queda lliure: si l'importador només mirés els actius, tornar a
 * pujar una plantilla que en porta un de baixa el crearia una segona vegada amb
 * el mateix codi, i la base de dades ho acceptaria. El NIF va al revés: un
 * registre de baixa no ha de poder segrestar-lo, perquè qui mana és el viu.
 *
 * UNA CEL·LA BUIDA NO ESBORRA RES
 * -------------------------------
 * `provided` porta només els camps que el full menciona I omple. És la regla 2
 * de ./import, i aquí es fa complir a l'origen: una cel·la en blanc no hi entra,
 * així que no pot ser mai una diferència ni, per tant, una escriptura.
 */

import { z } from 'zod';
import type { ImportRowStatus } from '../enums/index.js';
import { parseDecimal } from '../format/index.js';
import { compileCodeFormat } from './codeFormat.js';

// ── El mestre, reduït al que l'identifica ────────────────────────────────────

/**
 * El mínim que un mestre ha d'exposar perquè aquest mòdul el sàpiga identificar.
 * Els registres reals (proveïdor, client) hi afegeixen els seus propis camps.
 */
export interface MasterIdentity {
  readonly code: string | null;
  readonly name: string;
  readonly tax_id: string | null;
  /** Fals quan està donat de baixa (deleted_at IS NOT NULL). */
  readonly active: boolean;
}

/**
 * Clau de comparació d'un codi: esporgat i en minúscules. Un obrador que copia
 * «cli-0007» d'un albarà es refereix al mateix client que «CLI-0007», i crear-ne
 * un de nou deixaria el mestre amb dos codis bessons.
 */
export function normalizeMasterCode(code: string | null | undefined): string {
  return (code ?? '').trim().toLowerCase();
}

/**
 * Clau de comparació d'un NIF/CIF: majúscules i només lletres i xifres. «B-12
 * 345 678», «b12345678» i «B.12345678» són la mateixa empresa a tot arreu menys
 * en una comparació literal de cadenes.
 */
export function normalizeMasterTaxId(taxId: string | null | undefined): string {
  return (taxId ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// ── Índex del mestre + identificació d'una fila ──────────────────────────────

/** Un registre del mestre, amb el seu id, tal com l'indexa l'importador. */
export interface IndexedMaster<R extends MasterIdentity> {
  readonly id: string;
  readonly record: R;
}

/**
 * El mestre indexat per les dues identitats. `ambiguousTaxIds` recull els NIF
 * que porten més d'un registre actiu: no es poden fer servir per identificar.
 */
export interface MasterImportIndex<R extends MasterIdentity> {
  readonly byCode: ReadonlyMap<string, IndexedMaster<R>>;
  readonly byTaxId: ReadonlyMap<string, IndexedMaster<R>>;
  readonly ambiguousTaxIds: ReadonlySet<string>;
}

/**
 * Indexa el mestre: TOTS (actius i de baixa) per codi, NOMÉS ELS ACTIUS per NIF.
 * Vegeu la capçalera del mòdul per què les dues identitats no es tracten igual.
 * El primer registre guanya la clau; dos actius amb el mateix NIF no desapareixen
 * de l'índex sense dir res, queden marcats com a ambigus.
 */
export function buildMasterImportIndex<R extends MasterIdentity>(
  items: readonly IndexedMaster<R>[],
): MasterImportIndex<R> {
  const byCode = new Map<string, IndexedMaster<R>>();
  const byTaxId = new Map<string, IndexedMaster<R>>();
  const ambiguousTaxIds = new Set<string>();

  for (const item of items) {
    const codeKey = normalizeMasterCode(item.record.code);
    if (codeKey !== '' && !byCode.has(codeKey)) byCode.set(codeKey, item);

    if (!item.record.active) continue;
    const taxKey = normalizeMasterTaxId(item.record.tax_id);
    if (taxKey === '') continue;
    if (byTaxId.has(taxKey)) {
      ambiguousTaxIds.add(taxKey);
    } else {
      byTaxId.set(taxKey, item);
    }
  }

  return { byCode, byTaxId, ambiguousTaxIds };
}

/** Com s'ha identificat (o no) una fila contra el mestre. */
export interface MasterMatch<R extends MasterIdentity> {
  readonly matched: IndexedMaster<R> | undefined;
  /** Per quina dada s'ha identificat. 'none' quan és nou o no es pot comprovar. */
  readonly by: 'code' | 'tax_id' | 'none';
  /** Cert quan el NIF de la fila porta a més d'un registre actiu: no es decideix. */
  readonly ambiguous: boolean;
  /**
   * Cert quan la fila no porta ni codi ni NIF: es crearà, però ningú pot
   * garantir que no sigui un duplicat del que ja hi ha.
   */
  readonly unidentifiable: boolean;
}

/** Identifica una fila contra el mestre: primer pel codi, després pel NIF. */
export function matchMasterRow<R extends MasterIdentity>(
  index: MasterImportIndex<R>,
  row: { readonly code?: string | null; readonly tax_id?: string | null },
): MasterMatch<R> {
  const codeKey = normalizeMasterCode(row.code);
  if (codeKey !== '') {
    return {
      matched: index.byCode.get(codeKey),
      by: index.byCode.has(codeKey) ? 'code' : 'none',
      ambiguous: false,
      unidentifiable: false,
    };
  }

  const taxKey = normalizeMasterTaxId(row.tax_id);
  if (taxKey === '') {
    return { matched: undefined, by: 'none', ambiguous: false, unidentifiable: true };
  }
  if (index.ambiguousTaxIds.has(taxKey)) {
    return { matched: undefined, by: 'none', ambiguous: true, unidentifiable: false };
  }
  const matched = index.byTaxId.get(taxKey);
  return {
    matched,
    by: matched ? 'tax_id' : 'none',
    ambiguous: false,
    unidentifiable: false,
  };
}

/**
 * La clau amb què dues files del MATEIX fitxer xoquen, o cadena buida quan la
 * fila no es pot identificar (i per tant no xoca amb ningú). El prefix manté
 * separats els espais de noms: un codi «B12345678» no és el NIF «B12345678».
 */
export function masterRowIdentity(row: {
  readonly code?: string | null;
  readonly tax_id?: string | null;
}): string {
  const codeKey = normalizeMasterCode(row.code);
  if (codeKey !== '') return `code:${codeKey}`;
  const taxKey = normalizeMasterTaxId(row.tax_id);
  return taxKey === '' ? '' : `tax:${taxKey}`;
}

// ── El codi d'una alta ha de seguir la màscara de l'obrador (OBR-122) ────────

/** Una fila que porta un codi, davant la màscara de codis del seu mestre. */
export interface MasterCodeCheck {
  /** El codi tal com el porta el full (ja esporgat); buit o absent = no en porta. */
  readonly code: string | null | undefined;
  /** Cert quan la fila no ha identificat cap registre: és una ALTA. */
  readonly isNew: boolean;
  /** La màscara de codis de l'obrador per a aquest mestre («PRV-{seq:04}»). */
  readonly format: string;
  /** L'espai de noms i18n de l'importador que ho ensenya. */
  readonly namespace: string;
}

/**
 * L'observació d'ERROR quan una ALTA porta un codi que no té la forma de la màscara de l'obrador, o
 * `undefined` quan tot és en ordre.
 *
 * NOMÉS ALS CODIS NOUS. Un codi que ja és al mestre identifica el seu registre (i pot ser d'abans que
 * es canviés la màscara): jutjar-lo ara faria impossible tornar a pujar una plantilla amb les dades
 * actuals. I una fila sense codi no es toca: el servidor li'n genera un amb la màscara i la seqüència
 * de l'obrador. El codi MAI es corregeix sol: la fila s'atura i diu quin és el format esperat.
 *
 * El missatge porta el camp (`fieldKey`, que es tradueix sol), el codi que s'ha rebutjat i un exemple
 * del que la màscara genera; la fila a què va lligat la mostra qui el pinta (la revisió).
 */
export function masterCodeFormatMessage(check: MasterCodeCheck): ImportMessage | undefined {
  const code = (check.code ?? '').trim();
  if (!check.isNew || code === '') return undefined;
  const compiled = compileCodeFormat(check.format);
  if (compiled.pattern.test(code)) return undefined;
  return {
    key: `${check.namespace}:msgCodeFormat`,
    params: { fieldKey: `${check.namespace}:field_code`, code, example: compiled.example },
  };
}

// ── El NIF ja és d'algú altre ────────────────────────────────────────────────

/** Qui ja té reclamat un NIF: un registre del mestre, o una fila anterior del full. */
export type TaxIdOwner =
  | { readonly kind: 'record'; readonly id: string; readonly name: string }
  | { readonly kind: 'row'; readonly rowNumber: number };

/** Una fila, reduïda al que cal per saber amb quin NIF acabarà. */
export interface TaxIdClaim {
  readonly rowNumber: number;
  readonly taxId: string | null | undefined;
  /** El registre que la fila ha identificat, si n'hi ha. */
  readonly matchedId: string | undefined;
}

/**
 * Les files que volen escriure un NIF que ja és d'un ALTRE.
 *
 * Existeix perquè els dos mestres tenen el NIF únic per obrador entre els
 * actius. Deixar passar una fila així la convertiria en una violació d'índex a
 * mig run —un missatge que no diu ni quina fila ni amb qui xoca—, que és
 * justament el que la revisió existeix per evitar.
 *
 * Una fila que ja ÉS la propietària del NIF no xoca amb ella mateixa; una fila
 * sense NIF no reclama res; i dins del fitxer sempre guanya la primera.
 */
export function findTaxIdConflicts<R extends MasterIdentity>(
  claims: readonly TaxIdClaim[],
  index: MasterImportIndex<R>,
): ReadonlyMap<number, TaxIdOwner> {
  const owners = new Map<string, TaxIdOwner>();
  for (const [taxKey, item] of index.byTaxId) {
    owners.set(taxKey, { kind: 'record', id: item.id, name: item.record.name });
  }

  const conflicts = new Map<number, TaxIdOwner>();
  for (const claim of claims) {
    const taxKey = normalizeMasterTaxId(claim.taxId);
    if (taxKey === '') continue;

    const owner = owners.get(taxKey);
    if (owner === undefined) {
      owners.set(taxKey, { kind: 'row', rowNumber: claim.rowNumber });
      continue;
    }
    if (owner.kind === 'record' && owner.id === claim.matchedId) continue;
    conflicts.set(claim.rowNumber, owner);
  }
  return conflicts;
}

// ── Un sol marcat per grup ───────────────────────────────────────────────────

/** Una fila que demana ser l'única marcada del seu grup. */
export interface ExclusiveFlagClaim {
  readonly rowNumber: number;
  /** El grup dins del qual el marcat és exclusiu (un client, un article…). */
  readonly groupKey: string;
  readonly flag: boolean;
}

/**
 * Les files que demanen el marcat quan una altra del MATEIX full ja el demanava
 * per al mateix grup. Retorna, per número de fila, la fila amb què xoquen; la
 * primera sempre guanya.
 *
 * Existeix perquè aquests marcats tenen un índex únic parcial al darrere (una
 * adreça predeterminada per client, un proveïdor principal per article): dues
 * files marcades acabarien en una violació d'índex a mig escriure, i el segon
 * escriptor no sabria ni contra qui ha xocat.
 *
 * Que un registre passi a ser el marcat quan el grup ja en tenia un altre NO és
 * un xoc: és un canvi, i qui escriu ha de treure primer el que hi havia.
 */
export function findExclusiveFlagConflicts(
  claims: readonly ExclusiveFlagClaim[],
): ReadonlyMap<number, number> {
  const firstByGroup = new Map<string, number>();
  const conflicts = new Map<number, number>();

  for (const claim of claims) {
    if (!claim.flag) continue;
    const first = firstByGroup.get(claim.groupKey);
    if (first === undefined) {
      firstByGroup.set(claim.groupKey, claim.rowNumber);
    } else {
      conflicts.set(claim.rowNumber, first);
    }
  }
  return conflicts;
}

// ── El contacte estructurat ──────────────────────────────────────────────────

/** El contacte tal com viu a `contact_json` (clients, suppliers, punts d'enviament). */
export interface MasterContactJson {
  contact_name?: string;
  phone?: string;
  email?: string;
}

/** Les claus aplanades que, en realitat, viuen dins de `contact_json`. */
export const MASTER_CONTACT_KEYS = ['contact_name', 'phone', 'email'] as const;
export type MasterContactKey = (typeof MASTER_CONTACT_KEYS)[number];

export function isMasterContactKey(key: string): key is MasterContactKey {
  return (MASTER_CONTACT_KEYS as readonly string[]).includes(key);
}

/**
 * Torna a plegar el contacte: pren el que ja hi ha desat i hi sobreposa NOMÉS
 * les claus que el full aporta. Sobreescriure `contact_json` sencer amb el que
 * porta la fila esborraria el telèfon cada cop que s'importa un full que només
 * en du el correu — la cel·la absent hauria esborrat una dada.
 */
export function mergeMasterContact(
  existing: Readonly<MasterContactJson> | null | undefined,
  provided: Readonly<Record<string, unknown>>,
): MasterContactJson {
  const merged: MasterContactJson = { ...(existing ?? {}) };
  for (const key of MASTER_CONTACT_KEYS) {
    const value = provided[key];
    if (typeof value === 'string' && value.trim() !== '') merged[key] = value.trim();
  }
  return merged;
}

/**
 * Converteix els camps aplanats que el full aporta en columnes de la taula: el
 * contacte es plega (fusionat amb l'existent) i les claus de `drop` no hi surten
 * (l'estat, per exemple, no és una columna sinó una baixa lògica).
 */
export function masterColumnsFromProvided(
  provided: Readonly<Record<string, unknown>>,
  existingContact?: Readonly<MasterContactJson> | null,
  drop: readonly string[] = ['active'],
): Readonly<Record<string, unknown>> {
  const dropped = new Set(drop);
  const columns: Record<string, unknown> = {};
  let touchesContact = false;

  for (const [key, value] of Object.entries(provided)) {
    if (dropped.has(key)) continue;
    if (isMasterContactKey(key)) {
      touchesContact = true;
      continue;
    }
    columns[key] = value;
  }

  if (touchesContact) {
    columns.contact_json = mergeMasterContact(existingContact, provided);
  }
  return columns;
}

// ── Projecció d'una fila ─────────────────────────────────────────────────────

/**
 * Com es coerciona una cel·la cap al seu camp. `custom` és per als camps que un
 * mestre concret llegeix amb una regla pròpia (dies d'entrega, canal de comandes…):
 * porten la seva funció `parse` i aquest mòdul només l'executa.
 */
export type MasterFieldKind = 'text' | 'email' | 'status' | 'boolean' | 'number' | 'custom';

/**
 * El resultat de llegir una cel·la d'un camp `custom`: el valor ja convertit, o
 * una observació (clau i18n + paràmetres). Un valor que és una llista BUIDA no
 * entra a `provided` — igual que una cel·la en blanc, no diu res.
 */
export type MasterCellParse =
  | { readonly ok: true; readonly value: unknown }
  | { readonly ok: false; readonly message: ImportMessage };

/**
 * Un camp importable: la columna del full i la propietat que omple. Parametritzat
 * per la clau perquè cada importador conservi el seu tipus estret de camps i el
 * compilador hi pugui comprovar les cel·les que se li passen.
 */
export interface MasterImportField<K extends string = string> {
  /** Clau del camp (i de la columna a la plantilla). */
  readonly key: K;
  /**
   * La propietat del registre que omple. Coincideix amb `key` excepte per a
   * l'estat, que omple `active`: a la base de dades l'estat no és una columna.
   */
  readonly target: string;
  readonly kind: MasterFieldKind;
  /** Una fila sense aquest camp no es pot crear. */
  readonly required: boolean;
  /** Clau i18n de l'etiqueta visible (i de la capçalera de la plantilla). */
  readonly labelKey: string;
  /** Només per a `kind: 'custom'`: llegeix una cel·la NO buida. */
  readonly parse?: (cell: string) => MasterCellParse;
  /**
   * Columna INFORMATIVA: la plantilla la pot escriure per ajudar qui l'omple
   * («un Sac d'aquest article són 25 kg»), però no s'importa mai.
   *
   * Cal que sigui un camp i no simplement una columna desconeguda: una columna
   * que no es reconeix es reporta com a «no es farà servir», i un avís que surt a
   * cada viatge d'anada i tornada de la mateixa plantilla deixa de voler dir res.
   */
  readonly readOnly?: boolean;
}

/** Una observació sobre una fila: clau i18n + paràmetres, que resol qui la mostra. */
export interface ImportMessage {
  readonly key: string;
  readonly params?: Readonly<Record<string, string | number>>;
}

/** El resultat de llegir una fila del full. */
export interface MasterRowProjection {
  readonly status: ImportRowStatus;
  /** NOMÉS els camps que el full menciona i omple, amb els noms del registre. */
  readonly provided: Readonly<Record<string, unknown>>;
  readonly messages: readonly ImportMessage[];
}

/** Els tokens d'estat i de sí/no que es reconeixen, a més de les etiquetes traduïdes. */
const TRUE_TOKENS = new Set([
  'actiu', 'activo', 'active', 'alta', 'si', 'sí', 'yes', 'true', '1', 'x', 'y', 's',
]);
const FALSE_TOKENS = new Set([
  'inactiu', 'inactivo', 'inactive', 'baixa', 'baja', 'no', 'false', '0', 'n',
]);

/** Etiquetes de l'obrador per als valors sí/no i actiu/inactiu, en el seu idioma. */
export interface MasterProjectionLabels {
  readonly trueLabel?: string;
  readonly falseLabel?: string;
}

/** Interpreta una cel·la booleana (estat, sí/no). `undefined` = no es reconeix. */
export function parseMasterBoolean(
  cell: string,
  labels: MasterProjectionLabels = {},
): boolean | undefined {
  const value = cell.trim().toLowerCase();
  if (value === '') return undefined;
  if (labels.trueLabel && value === labels.trueLabel.trim().toLowerCase()) return true;
  if (labels.falseLabel && value === labels.falseLabel.trim().toLowerCase()) return false;
  if (TRUE_TOKENS.has(value)) return true;
  if (FALSE_TOKENS.has(value)) return false;
  return undefined;
}

/**
 * Llegeix una cel·la numèrica tolerant amb el que escriu un full en català o
 * castellà: coma decimal, espais i separadors de milers. `undefined` quan no ho
 * és — mai un 0 silenciós, que en un preu seria regalar la mercaderia.
 *
 * La regla viu a `parseDecimal` (format/), que és la mateixa que apliquen els
 * formularis: un preu escrit a mà i un preu vingut d'un full s'han de llegir
 * igual, o el mateix «1,05» voldria dir dues coses segons per on entra.
 */
export function parseMasterNumber(cell: string): number | undefined {
  return parseDecimal(cell);
}

const emailSchema = z.string().trim().email();

/** Opcions de projecció: l'espai de noms i18n dels missatges i les etiquetes. */
export interface MasterProjectionOptions {
  /** Espai de noms i18n dels missatges (p. ex. 'backoffice.clientImport'). */
  readonly namespace: string;
  /** Etiquetes d'estat («Actiu» / «Inactiu») en l'idioma de l'usuari. */
  readonly status?: MasterProjectionLabels;
  /** Etiquetes de sí/no («Sí» / «No»). */
  readonly boolean?: MasterProjectionLabels;
}

/**
 * Llegeix una fila del full (cel·les ja extretes per clau de camp) i la
 * converteix en el que l'importador pot fer servir.
 *
 * Severitat: falta un camp obligatori, un valor sí/no o d'estat que no s'entén,
 * o un correu que no és un correu → ERROR. La fila no escriu res. Guardar un
 * correu invàlid convertiria el mestre en una llista d'adreces que no serveixen
 * per enviar res, i endevinar un estat seria decidir per l'obrador.
 */
export function projectMasterRow<K extends string>(
  fields: readonly MasterImportField<K>[],
  cells: Readonly<Partial<Record<K, string>>>,
  options: MasterProjectionOptions,
): MasterRowProjection {
  const provided: Record<string, unknown> = {};
  const messages: ImportMessage[] = [];
  let hasError = false;

  for (const field of fields) {
    // Una columna informativa es reconeix (per no sortir com a desconeguda) però
    // no s'importa: ni es valida ni entra a `provided`.
    if (field.readOnly) continue;

    const cell = (cells[field.key] ?? '').trim();

    if (cell === '') {
      if (field.required) {
        hasError = true;
        messages.push({
          key: `${options.namespace}:msgRequiredMissing`,
          params: { fieldKey: field.labelKey },
        });
      }
      // Una cel·la buida no diu res, així que no entra a `provided`.
      continue;
    }

    switch (field.kind) {
      case 'status':
      case 'boolean': {
        const isStatus = field.kind === 'status';
        const value = parseMasterBoolean(
          cell,
          (isStatus ? options.status : options.boolean) ?? {},
        );
        if (value === undefined) {
          hasError = true;
          messages.push({
            key: `${options.namespace}:${isStatus ? 'msgInvalidStatus' : 'msgInvalidBoolean'}`,
            params: { fieldKey: field.labelKey, value: cell },
          });
        } else {
          provided[field.target] = value;
        }
        break;
      }
      case 'number': {
        const value = parseMasterNumber(cell);
        if (value === undefined || value < 0) {
          hasError = true;
          messages.push({
            key: `${options.namespace}:msgInvalidNumber`,
            params: { fieldKey: field.labelKey, value: cell },
          });
        } else {
          provided[field.target] = value;
        }
        break;
      }
      case 'email': {
        if (!emailSchema.safeParse(cell).success) {
          hasError = true;
          // Amb la columna: un mestre pot tenir més d'un correu (el general i el de qualitat).
          messages.push({
            key: `${options.namespace}:msgInvalidEmail`,
            params: { fieldKey: field.labelKey, value: cell },
          });
        } else {
          provided[field.target] = cell;
        }
        break;
      }
      case 'text': {
        provided[field.target] = cell;
        break;
      }
      case 'custom': {
        const parsed: MasterCellParse = field.parse
          ? field.parse(cell)
          : { ok: true, value: cell };
        if (!parsed.ok) {
          hasError = true;
          // L'etiqueta del camp s'afegeix aquí, com a la resta de missatges: qui parseja
          // no ha de saber quina columna és.
          messages.push({
            key: parsed.message.key,
            params: { fieldKey: field.labelKey, ...(parsed.message.params ?? {}) },
          });
        } else if (!(Array.isArray(parsed.value) && parsed.value.length === 0)) {
          provided[field.target] = parsed.value;
        }
        break;
      }
      default: {
        const exhaustive: never = field.kind;
        return exhaustive;
      }
    }
  }

  return { status: hasError ? 'error' : 'valid', provided, messages };
}

/**
 * Resol una observació a text llegible.
 *
 * Els missatges viatgen com a CLAU + paràmetres perquè el domini no depengui de
 * l'idioma de qui el crida. Un d'aquells paràmetres, `fieldKey`, és al seu torn
 * una clau i18n —l'etiqueta del camp—, i traduir-la és responsabilitat d'aquí i
 * no de cada pantalla: quan cada importador se'n cuidava pel seu compte,
 * n'hi va haver prou amb oblidar-ne un per ensenyar-li a un obrador
 * «Valor numèric no vàlid a backoffice.supplierArticleImport:field_last_price».
 *
 * `translate` és el `t` d'i18next, passat com a paràmetre per no lligar el
 * domini a la llibreria.
 */
export function resolveImportMessage(
  message: ImportMessage,
  translate: (key: string, params?: Readonly<Record<string, string | number>>) => string,
): string {
  const params: Record<string, string | number> = { ...(message.params ?? {}) };
  const fieldKey = params.fieldKey;
  if (typeof fieldKey === 'string') {
    params.field = translate(fieldKey);
    delete params.fieldKey;
  }
  return translate(message.key, params);
}

/**
 * Els sufixos de clau i18n que la importació d'un mestre pot emetre — els que
 * genera aquest mòdul i els que generen els cridants (xocs d'identitat).
 *
 * Existeix perquè un test per entitat els pugui comprovar contra el catàleg: les
 * claus de missatge se serveixen com a dades (`t(m.key, m.params)`), i una clau
 * computada no la revisa ni el typecheck ni el test d'ús d'i18n — un error
 * d'escriptura arribaria a producció com a text cru sobre la fila d'un obrador.
 */
export const MASTER_IMPORT_MESSAGE_SUFFIXES: readonly string[] = [
  'msgRequiredMissing',
  'msgInvalidStatus',
  'msgInvalidBoolean',
  'msgInvalidNumber',
  'msgInvalidEmail',
  'msgAmbiguousTaxId',
  'msgUnidentifiable',
  'msgDuplicateInFile',
  'msgCodeTaken',
  'msgCodeFormat',
  'msgTaxIdTaken',
  'msgTaxIdInFile',
];
